import { useEffect, useState } from 'react'
import { api, type GoldenSetName } from '../lib/api.ts'
import { goldenSetSummary } from '../lib/goldenSetOutcome.ts'
import { Button } from './Button.tsx'
import { Checkbox } from './Checkbox.tsx'
import { useToast } from './Toast.tsx'

/**
 * Choosing which golden set a conferência is copied into.
 *
 * There are two sets and they answer different questions, so which one a case
 * belongs in is a judgement -- and it was being made for people: the button
 * wrote both and then opened the reading case, so the comparison case looked
 * like something the button had never done. Two questions, two boxes, and the
 * words for what each is measuring.
 *
 * **Drafts, never gabaritos.** Both copies start as the model's own output,
 * which is the thing under test: a draft nobody corrected is a measurement of
 * whether we agree with ourselves.
 */
export interface GoldenSetPresence {
  readonly caseId: string
  /** Without a reading there is nothing to copy into either set. */
  readonly hasReading: boolean
  /** A clone with no credentials cannot write a case anywhere. */
  readonly configured: boolean
  readonly reader: boolean
  readonly resolution: boolean
}

const WHAT_IT_HOLDS: Readonly<Record<GoldenSetName, { title: string; body: string }>> = {
  reader: {
    title: 'Caso de leitura',
    body: 'Guarda as páginas da receita e o que o leitor entendeu — preparações, ingredientes, doses, paciente e prescritor — com a região de cada valor na imagem. Mede se lemos a receita certo.',
  },
  resolution: {
    title: 'Caso de comparação',
    body: 'Guarda cada termo prescrito ao lado do item que o ERP tem digitado e o que a conferência concluiu. Sem imagens e sem dados do paciente. Mede se acusamos a divergência certa.',
  },
}

/**
 * The dialog itself, given what the sets already hold.
 *
 * Separate from the fetching below so it can be rendered in a test with any
 * combination of "already there", which is the part with the rules in it.
 */
export function AddToGoldenSetDialog({
  presence,
  busy,
  onAdd,
  onClose,
}: {
  presence: GoldenSetPresence
  busy: boolean
  onAdd: (sets: readonly GoldenSetName[]) => void
  onClose: () => void
}) {
  /*
   * Everything it can still take, ticked.
   *
   * The common case is a case worth having in both, and the dialog exists to
   * make the *other* choice possible rather than to make the ordinary one an
   * exercise. A set that already holds this conferência starts unticked,
   * because ticking it would promise something that will not happen.
   */
  const [chosen, setChosen] = useState<ReadonlySet<GoldenSetName>>(
    () =>
      new Set(
        (['reader', 'resolution'] as const).filter(
          (set) => presence.hasReading && presence.configured && !presence[set],
        ),
      ),
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const blocked = !presence.hasReading
    ? 'Esta conferência não tem leitura, então não há o que levar para o golden set.'
    : !presence.configured
      ? 'O golden set não está configurado neste ambiente.'
      : undefined

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="golden-set-title"
        className="bg-background-raised rounded-base shadow-raised border-stroke-weak flex w-full max-w-[34rem] flex-col gap-4 border p-5"
      >
        <div className="flex flex-col gap-1">
          <h2 id="golden-set-title" className="text-tPSemi text-text-strong">
            Adicionar ao golden set
          </h2>
          <p className="text-tP text-text-weak">
            Cria rascunhos a partir desta conferência, com o mesmo id nos dois conjuntos. O que está
            lá vira gabarito só depois de alguém revisar e marcar como corrigido.
          </p>
        </div>

        {blocked !== undefined && <p className="text-tP text-text-error">{blocked}</p>}

        <div className="flex flex-col gap-2">
          {(['reader', 'resolution'] as const).map((set) => {
            const already = presence[set]
            const disabled = already || blocked !== undefined || busy
            return (
              <label
                key={set}
                className={`rounded-base border-stroke-weak flex gap-3 border p-3 ${
                  disabled ? 'opacity-70' : 'hover:bg-fill-weak cursor-pointer'
                }`}
              >
                <Checkbox
                  checked={chosen.has(set)}
                  disabled={disabled}
                  aria-label={WHAT_IT_HOLDS[set].title}
                  onChange={() =>
                    setChosen((current) => {
                      const next = new Set(current)
                      if (next.has(set)) next.delete(set)
                      else next.add(set)
                      return next
                    })
                  }
                />
                <span className="flex flex-col gap-1">
                  <span className="text-tPSemi text-text-strong">{WHAT_IT_HOLDS[set].title}</span>
                  <span className="text-tP text-text-weak">{WHAT_IT_HOLDS[set].body}</span>
                  {already && (
                    /* What is there may already have been corrected, and this
                       never overwrites it. */
                    <span className="text-tP text-text-weak italic">
                      Esta conferência já está neste conjunto. O que existe não será alterado.
                    </span>
                  )}
                </span>
              </label>
            )
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" tone="neutral" size="small" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            tone="brand"
            size="small"
            disabled={chosen.size === 0 || busy}
            onClick={() => onAdd([...chosen])}
          >
            {busy ? 'Adicionando…' : 'Adicionar'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * The dialog, wired: what each set already holds, and what to do with an answer.
 *
 * Opens the case it created afterwards, because correcting the draft is the
 * next act -- the reading case when that is one of them, since it is the one
 * with the images to check against.
 */
export function AddToGoldenSet({
  verificationId,
  onClose,
  onOpenCase,
  onOpenResolutionCase,
}: {
  verificationId: string
  onClose: () => void
  onOpenCase?: ((caseId: string) => void) | undefined
  onOpenResolutionCase?: ((caseId: string) => void) | undefined
}) {
  const toast = useToast()
  const [presence, setPresence] = useState<GoldenSetPresence | undefined>()
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .goldenSetForVerification(verificationId)
      .then((answer) => {
        if (!cancelled) setPresence(answer)
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        toast.show('Não foi possível ler o golden set.', {
          tone: 'error',
          detail: cause instanceof Error ? cause.message : undefined,
        })
        onClose()
      })
    return () => {
      cancelled = true
    }
    // Once per conferência: the dialog is opened, answered and closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationId])

  // Nothing is drawn until the answer arrives: a dialog whose boxes rearrange
  // themselves a moment after it opens is one people tick twice.
  if (presence === undefined) return null

  return (
    <AddToGoldenSetDialog
      presence={presence}
      busy={busy}
      onClose={onClose}
      onAdd={(sets) => {
        setBusy(true)
        api
          .addVerificationToGoldenSet(verificationId, sets)
          .then((result) => {
            const summary = goldenSetSummary({
              reader: result.reader,
              resolution: result.resolution,
            })
            toast.show(summary.message, {
              tone: 'success',
              detail:
                result.missingPages > 0
                  ? `${result.missingPages} página(s) não estavam guardadas e ficaram de fora.`
                  : summary.detail,
            })
            onClose()
            if (sets.includes('reader')) onOpenCase?.(result.caseId)
            else onOpenResolutionCase?.(result.caseId)
          })
          .catch((cause: unknown) => {
            toast.show('Não foi possível adicionar ao golden set.', {
              tone: 'error',
              detail: cause instanceof Error ? cause.message : undefined,
            })
          })
          .finally(() => setBusy(false))
      }}
    />
  )
}
